import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Star, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface SatisfactionSurveyProps {
  token: string;
  onSuccess: () => void;
}

interface SurveyData {
  overallRating: number;
  serviceQuality: number;
  communication: number;
  wouldRecommend: boolean | null;
  comments: string;
}

export function SatisfactionSurvey({ token, onSuccess }: SatisfactionSurveyProps) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SurveyData>({
    overallRating: 0,
    serviceQuality: 0,
    communication: 0,
    wouldRecommend: null,
    comments: '',
  });

  const StarRating = ({
    value,
    onChange,
    label,
  }: {
    value: number;
    onChange: (value: number) => void;
    label: string;
  }) => {
    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => onChange(star)}
              className="focus:outline-none transition-colors"
            >
              <Star
                className={`h-8 w-8 ${
                  star <= value
                    ? 'fill-yellow-400 text-yellow-400'
                    : 'fill-gray-200 text-gray-200'
                }`}
              />
            </button>
          ))}
        </div>
        {value > 0 && (
          <p className="text-sm text-muted-foreground">
            {value === 1 && 'Muito insatisfeito'}
            {value === 2 && 'Insatisfeito'}
            {value === 3 && 'Neutro'}
            {value === 4 && 'Satisfeito'}
            {value === 5 && 'Muito satisfeito'}
          </p>
        )}
      </div>
    );
  };

  const handleSubmit = async () => {
    if (data.overallRating === 0 || data.serviceQuality === 0 || data.communication === 0 || data.wouldRecommend === null) {
      toast.error('Por favor, responda todas as perguntas obrigatórias');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('token', token);
      formData.append('submission_type', 'text');
      formData.append('text_content', 'Pesquisa de Satisfação');
      formData.append('metadata', JSON.stringify({
        type: 'satisfaction_survey',
        responses: data,
      }));

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit-assisted-collection`,
        {
          method: 'POST',
          body: formData,
        }
      );

      const result = await response.json();

      if (result.error) {
        throw new Error(result.error);
      }

      toast.success('Pesquisa enviada com sucesso! Obrigado pelo seu feedback.');
      onSuccess();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao enviar pesquisa');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pesquisa de Satisfação</CardTitle>
        <CardDescription>
          Sua opinião é muito importante para nós. Por favor, compartilhe sua experiência com o atendimento.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <StarRating
          value={data.overallRating}
          onChange={(value) => setData({ ...data, overallRating: value })}
          label="Nota Geral *"
        />

        <StarRating
          value={data.serviceQuality}
          onChange={(value) => setData({ ...data, serviceQuality: value })}
          label="Qualidade do Atendimento *"
        />

        <StarRating
          value={data.communication}
          onChange={(value) => setData({ ...data, communication: value })}
          label="Comunicação do Profissional *"
        />

        <div className="space-y-3">
          <Label>Você recomendaria este profissional? *</Label>
          <RadioGroup
            value={data.wouldRecommend === null ? '' : data.wouldRecommend ? 'yes' : 'no'}
            onValueChange={(value) =>
              setData({
                ...data,
                wouldRecommend: value === 'yes' ? true : value === 'no' ? false : null,
              })
            }
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id="recommend-yes" />
              <Label htmlFor="recommend-yes" className="cursor-pointer">
                Sim, recomendaria
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id="recommend-no" />
              <Label htmlFor="recommend-no" className="cursor-pointer">
                Não recomendaria
              </Label>
            </div>
          </RadioGroup>
        </div>

        <div className="space-y-2">
          <Label>Comentários Adicionais (opcional)</Label>
          <Textarea
            value={data.comments}
            onChange={(e) => setData({ ...data, comments: e.target.value })}
            placeholder="Compartilhe mais detalhes sobre sua experiência, sugestões de melhoria ou elogios..."
            rows={5}
          />
        </div>

        <Button
          onClick={handleSubmit}
          disabled={loading || data.overallRating === 0 || data.serviceQuality === 0 || data.communication === 0 || data.wouldRecommend === null}
          className="w-full"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Enviando...
            </>
          ) : (
            'Enviar Pesquisa'
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

